import { LazyAudioPlayer } from "@circle-react-shared/EditorContentBlocks/AudioPlayer/LazyAudioPlayer";
import { FileCard } from "@circle-react-shared/EditorContentBlocks/FileCard";
import type { ReactPortal, RefObject } from "react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { useCircleV4 } from "@/react/hooks/useCircleV4";

const renderBlock = (figure: HTMLElement): ReactPortal | null => {
  const {
    blobContentType: contentType,
    blobUrl: url,
    blobFilename: filename,
    blobBytesize: byteSize,
    blobSgid: sgid,
  } = figure.dataset;

  if (!contentType || !url || !filename) return null;

  const isAudio = contentType.startsWith("audio");
  const isPDF = contentType === "application/pdf";
  const isPlainFile =
    figure.classList.contains("attachment--file") &&
    !contentType.startsWith("image") &&
    !contentType.startsWith("video");

  if (!isAudio && !isPlainFile && !isPDF) return null;

  // data-blob-filename serializes ActiveStorage::Filename as JSON, which
  // wraps the name in literal quotes.
  const cleanFilename = filename.replace(/^"(.*)"$/, "$1");

  const previewImageUrl = isPDF
    ? (figure.querySelector<HTMLElement>("[data-background-image]")?.dataset
        .backgroundImage ??
      figure
        .querySelector<HTMLElement>(".attachment__preview")
        ?.style.backgroundImage?.replace(/^url\("?|"?\)$/g, ""))
    : undefined;

  const host = document.createElement("div");
  host.setAttribute("contenteditable", "false");
  host.className = "my-3";
  figure.removeAttribute("class");
  figure.removeAttribute("style");
  figure.style.width = "100%";
  figure.style.maxWidth = "100%";
  figure.style.margin = "0";
  figure.replaceChildren(host);

  return createPortal(
    isAudio ? (
      <LazyAudioPlayer
        src={url}
        title={cleanFilename}
        downloadUrl={url}
        signedId={sgid}
        preload="metadata"
      />
    ) : (
      <FileCard
        file={{
          content_type: contentType,
          filename: cleanFilename,
          url,
          byte_size: byteSize ? Number(byteSize) : undefined,
        }}
        previewUrl={previewImageUrl}
        onPreview={
          previewImageUrl
            ? () => window.open(url, "_blank", "noreferrer")
            : undefined
        }
      />
    ),
    host,
  );
};

/**
 * On Circle V4, replaces native Trix attachment markup (plain files, PDFs,
 * and audio) with the shared EditorContentBlocks components so Trix and
 * TipTap posts render attachments identically. Images and videos keep the
 * native markup (gallery/zoom behavior lives there).
 */
export const useCompassTrixBlocks = (
  ref: RefObject<HTMLDivElement | null>,
  content: string,
): ReactPortal[] => {
  const { isCircleV4 } = useCircleV4();
  const [portals, setPortals] = useState<ReactPortal[]>([]);

  useEffect(() => {
    if (!isCircleV4 || !ref.current) return;

    const figures = Array.from(
      ref.current.querySelectorAll<HTMLElement>(
        "figure[data-blob-content-type]",
      ),
    );

    setPortals(
      figures
        .map(renderBlock)
        .filter((portal): portal is ReactPortal => portal !== null),
    );
  }, [isCircleV4, ref, content]);

  return portals;
};
